import React, { useState } from 'react';

import { EventArea } from '../../../analytics-library/entry/sendEvent';
import { ClickType } from '../../../sharedModules/getClickType';
import { getWidgetInitialiser } from '../../../sharedModules/getWidgetInitialiser';
import { LegacyEventType } from '../../../types/LegacyEventType';
import { loadCSS } from '../../../utils/loadCSS';
import { prepareTabletopWidgetProps } from '../../../utils/prepareTabletopWidgetProps';
import { WidgetResult } from '../../hooks/useAnalytics';
import { InitialiserProps } from '../../types/InitialiserProps';
import type { WidgetProps } from '../../types/WidgetProps';

import styles from './styles/tabletop.css';
import TabletopBlock from './TabletopBlock';
import TabletopFooter from './TabletopFooter';
import { TabletopHeader } from './TabletopHeader';

loadCSS('tabletop');

const Tabletop: React.FC<WidgetProps> = (props) => {
  const {
    genericSharedComponents: { HawkWidgetStandard },
    tabletopBlocks = [],
    translate,
    sendAnalytics,
    dealData,
    site,
  } = props;

  const minVisibleCount = 5;
  const [visibleCount, setVisibleCount] = useState(minVisibleCount);

  const visibleBlocks = tabletopBlocks.filter((row) =>
    Boolean(row.buyingGuideBlock.modelName || row.buyingGuideBlock.productName),
  );

  if (!visibleBlocks.length) {
    return null;
  }

  const showMore = (): void => {
    sendAnalytics({
      event: {
        clickType: ClickType.INTERNAL,
        type: LegacyEventType.CLICK_FROM,
        areaClicked: 'show more',
        label: 'Opened more offers',
      },
      dealData,
    });
    setVisibleCount(visibleBlocks.length);
  };

  const showLess = (): void => {
    sendAnalytics({
      event: {
        clickType: ClickType.INTERNAL,
        type: LegacyEventType.CLICK_FROM,
        areaClicked: 'show more',
        label: 'Closed more offers',
      },
      dealData,
    });
    setVisibleCount(Math.min(minVisibleCount, visibleBlocks.length));
  };

  const onReadMoreClick = (offerId?: number): void => {
    const deal = dealData.deals.find((d) => d.id === offerId);
    sendAnalytics({
      event: {
        clickType: ClickType.INTERNAL,
        type: LegacyEventType.CLICK_FROM,
        areaClicked: EventArea.READ_MORE,
      },
      deal,
      dealData,
    });
  };
  const blocksToRender = visibleBlocks.slice(0, visibleCount);

  return (
    <HawkWidgetStandard {...props}>
      <div className={styles['container']}>
        <TabletopHeader site={site} translate={translate} />
        {blocksToRender.map((row) => (
          <TabletopBlock key={`${row.modelName}`} row={row} onReadMoreClick={onReadMoreClick} />
        ))}
        <TabletopFooter
          visibleCount={visibleCount}
          minVisibleCount={minVisibleCount}
          maxVisibleCount={visibleBlocks.length}
          showMore={showMore}
          showLess={showLess}
        />
      </div>
    </HawkWidgetStandard>
  );
};

Tabletop.getInitialiserProps = async ({
  renderProps,
  attributes,
  getWidgetResponse,
  getModelOffersResponse,
  getSeasonalResponse,
  getAiredaleFeeds,
  getModelSuggestions,
  postForWidgetResponse,
  editorial,
  defaultParams,
  origin,
  dataLinkMerchant,
}): InitialiserProps => {
  const result = await getWidgetInitialiser({
    editorial,
    defaultParams,
    origin,
    dataLinkMerchant,
    getWidgetResponse,
    getModelOffersResponse,
    getSeasonalResponse,
    getAiredaleFeeds,
    getModelSuggestions,
    postForWidgetResponse,
    attributes,
    ...renderProps,
  });

  const props = result.props.hawkJson.buyingGuideBlocksData?.length
    ? await prepareTabletopWidgetProps(result.props, renderProps.site, getAiredaleFeeds)
    : result.props;

  return {
    ...result,
    props,
    result: props.tabletopBlocks?.length ? WidgetResult.SUCCESS : result.result,
  };
};

export default Tabletop;
